/**
 * SectionButtons — unified button row for legacy block types (About / Hero /
 * Cta / Gallery / Testimonial / etc.) that historically only rendered
 * `content._style.buttons` via <StyleButtons>.
 *
 * When the user clicks "+ ADD BLOCK → Button" on a section, the new flow in
 * DashboardContent.js writes to `content.buttons` (the modern, premium-ext
 * compatible field). This component renders BOTH sources so the button shows
 * up immediately right under the section text without breaking any legacy
 * buttons stored in `_style.buttons`.
 *
 * Iteration 140 — fix for "button added to a section creates a new section
 * instead of landing under the text" (user report Feb 19 2026).
 */
import React from 'react';
import { getWbButtonAttrs } from './WbButton';

function ButtonRow({ buttons, field, defaultColor = '#8B5CF6' }) {
  if (!buttons?.length) return null;
  return (
    <div className="flex gap-3 justify-center mt-6 flex-wrap" data-wb-buttons-row={field}>
      {buttons.map((btn, i) => {
        const attrs = getWbButtonAttrs(i, field);
        return (
          <a
            key={i}
            href={btn.link || '#'}
            target={btn.link && btn.link.startsWith('http') ? '_blank' : '_self'}
            rel="noopener noreferrer"
            {...attrs}
            className={`px-6 py-3 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 ${attrs.className}`}
            style={{ backgroundColor: btn.color || defaultColor }}
          >
            {btn.text}
          </a>
        );
      })}
    </div>
  );
}

/**
 * Renders all section-level button rows for a block.
 *
 * Usage in any legacy block renderer:
 *   <SectionButtons block={block} />
 *
 * Replaces the older pattern:
 *   <StyleButtons buttons={s.buttons} />
 */
export default function SectionButtons({ block }) {
  const c = block?.content || {};
  const styleButtons   = c._style?.buttons;   // legacy field
  const contentButtons = c.buttons;            // modern field (where new "+ Add Button" writes)
  return (
    <>
      <ButtonRow buttons={styleButtons}   field="_style.buttons" />
      <ButtonRow buttons={contentButtons} field="buttons" />
    </>
  );
}
